import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, ArrowLeft, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      console.log('🔑 Dashboard - Token from localStorage:', token ? 'exists' : 'NOT FOUND');
      
      const config = {
        withCredentials: true
      };
      
      // Add Authorization header if token exists
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
        console.log('✅ Dashboard - Added Authorization header');
      }
      
      const [statsRes, analysesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, config),
        axios.get(`${API}/analyses?limit=10`, config)
      ]);
      setStats(statsRes.data);
      setAnalyses(analysesRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view your analysis history');
        navigate('/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            data-testid="back-button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Dashboard</h1>
          </div>
          <Button
            data-testid="analyzer-button"
            onClick={() => navigate('/analyzer')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            New Analysis
          </Button>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading dashboard...</p>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-6">
                <Card data-testid="total-analyses-card" className="card-elevated">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Analyses</CardTitle>
                    <BarChart3 className="w-4 h-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{stats?.total_analyses || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">Emotional insights generated</p>
                  </CardContent>
                </Card>

                <Card data-testid="top-emotion-card" className="card-elevated">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Top Emotion</CardTitle>
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900 capitalize">
                      {stats?.dominant_emotions ? Object.entries(stats.dominant_emotions).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A' : 'N/A'}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Most frequent dominant emotion</p>
                  </CardContent>
                </Card>

                <Card data-testid="avg-arousal-card" className="card-elevated">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Avg Arousal</CardTitle>
                    <Users className="w-4 h-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">
                      {stats?.emotion_distribution ? Math.round(Object.values(stats.emotion_distribution).reduce((a, b) => a + b, 0) / Object.keys(stats.emotion_distribution).length) : 0}%
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Average emotional intensity</p>
                  </CardContent>
                </Card>
              </div>

              {/* Emotion Distribution */}
              {stats?.emotion_distribution && (
                <Card data-testid="emotion-distribution-card" className="card-elevated">
                  <CardHeader>
                    <CardTitle style={{fontFamily: 'Space Grotesk'}}>Emotion Distribution</CardTitle>
                    <CardDescription>Average emotional scores across all analyses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(stats.emotion_distribution)
                        .sort(([,a], [,b]) => b - a)
                        .map(([emotion, score]) => (
                          <div key={emotion} className="p-4 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100">
                            <p className="text-sm font-semibold text-gray-700 capitalize">{emotion}</p>
                            <p className="text-2xl font-bold text-indigo-600 mt-1">{Math.round(score)}%</p>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Analyses */}
              <Card data-testid="recent-analyses-card" className="card-elevated">
                <CardHeader>
                  <CardTitle style={{fontFamily: 'Space Grotesk'}}>Recent Analyses</CardTitle>
                  <CardDescription>Your latest emotional intelligence insights</CardDescription>
                </CardHeader>
                <CardContent>
                  {analyses.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No analyses yet. Start by analyzing some content!</p>
                      <Button
                        data-testid="start-analyzing-button"
                        onClick={() => navigate('/analyzer')}
                        className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
                      >
                        Start Analyzing
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {analyses.map((analysis) => (
                        <div
                          key={analysis.id}
                          data-testid={`analysis-${analysis.id}`}
                          onClick={() => navigate(`/analysis/${analysis.id}`)}
                          className="p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="capitalize">
                                {analysis.content_type}
                              </Badge>
                              <Badge className="bg-indigo-600 capitalize">
                                {analysis.dominant_emotion}
                              </Badge>
                              {analysis.enhancement && (
                                <Badge className="bg-purple-600 text-white text-xs">
                                  ✨ Enhanced
                                </Badge>
                              )}
                              {analysis.super_enhancement_photo && (
                                <Badge className="bg-pink-600 text-white text-xs">
                                  📸 Photo+
                                </Badge>
                              )}
                              {analysis.super_enhancement_creative && (
                                <Badge className="bg-indigo-600 text-white text-xs">
                                  🎨 Creative+
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(analysis.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {analysis.content_type === 'text' 
                              ? analysis.content 
                              : analysis.content_type === 'url'
                              ? `URL: ${analysis.content}`
                              : `Image: ${analysis.content}`
                            }
                          </p>
                          <div className="flex gap-2 mt-3">
                            {Object.entries(analysis.emotions)
                              .sort(([,a], [,b]) => b - a)
                              .slice(0, 3)
                              .map(([emotion, score]) => (
                                <span key={emotion} className="text-xs text-gray-500 capitalize">
                                  {emotion}: {Math.round(score)}%
                                </span>
                              ))}
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-indigo-600 font-medium">Click to view details →</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;